import { Controller } from "@hotwired/stimulus"

// Tab bar del account (web): resuelve el tab activo por URL y cambia el icono
// outline→fill, como una tab bar nativa. Corre en connect() porque el li de
// notificaciones es turbo-permanent: un "activo" del server quedaría congelado
// en él entre visitas (doc: _docs/features/accounts/account_tab_bar.md)
export default class extends Controller {
  static targets = ["link"]

  // connect() corre a mitad del render de Turbo, cuando el li turbo-permanent
  // de notificaciones todavía es un placeholder (se trasplanta al final del
  // render): su link puede faltar acá. turbo:load (data-action en el nav)
  // re-resuelve con el DOM completo.
  connect() {
    this.refresh()
    this.lastY = window.scrollY
    this.lastViewportH = window.innerHeight
    this.downAccum = 0
    this.upAccum = 0
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("touchstart", this.onTouchStart, { passive: true })
    window.addEventListener("touchmove", this.onTouchMove, { passive: true })
    this.#reveal()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("touchstart", this.onTouchStart)
    window.removeEventListener("touchmove", this.onTouchMove)
    // que el snapshot de cache no quede con la barra escondida
    this.#reveal()
  }

  // Señal primaria en mobile: el primer tramo del gesto lo consume el
  // navegador para esconder SU barra (scrollY ni se mueve ahí, no hay eventos
  // de scroll) — el touch es lo único que se mueve en ese tramo. En Safari
  // (colapso gradual, sigue entregando touchmove) la barra se esconde junto
  // con la del browser; en Chrome iOS (switch de dos tiempos que se traga el
  // gesto) la nuestra sale recién después del suyo — limitación aceptada
  // 2026-07-30, no hay señal web más temprana (ver doc)
  onTouchStart = (event) => {
    this.touchY = event.touches[0].clientY
    this.touchX = event.touches[0].clientX
  }

  onTouchMove = (event) => {
    if (this.touchY == null) return
    const touch = event.touches[0]
    const delta = this.touchY - touch.clientY // dedo hacia arriba = bajar por la página
    const deltaX = Math.abs(touch.clientX - this.touchX)
    this.touchY = touch.clientY
    this.touchX = touch.clientX
    if (deltaX > Math.abs(delta)) return // gesto horizontal (pills, carruseles)
    const limit = document.documentElement.scrollHeight - window.innerHeight
    if (limit < 100) return // página sin scroll real: la barra se queda
    if (delta > 0 && window.scrollY >= limit - 60) return // al final: se queda
    this.#track(delta)
  }

  // Fallback por scroll (rueda en desktop, momentum) + reveal en los topes.
  // Los eventos con el viewport redimensionándose se descartan: la barra del
  // browser animándose dispara deltas invertidos espurios que interrumpían
  // la transición a mitad de camino
  onScroll = () => {
    const y = window.scrollY
    const viewportH = window.innerHeight
    const delta = y - this.lastY
    this.lastY = y
    if (viewportH !== this.lastViewportH) {
      this.lastViewportH = viewportH
      return
    }
    const limit = document.documentElement.scrollHeight - viewportH
    if (y <= 0 || y >= limit - 1) {
      this.#reveal()
    } else {
      this.#track(delta)
    }
  }

  // Histéresis simétrica: 12px acumulados en una dirección esconden/muestran
  // (micro-movimientos y jitter no togglean la barra)
  #track(delta) {
    if (delta > 0) {
      this.upAccum = 0
      this.downAccum += delta
      if (this.downAccum > 12) this.#conceal()
    } else if (delta < 0) {
      this.downAccum = 0
      this.upAccum -= delta
      if (this.upAccum > 12) this.#reveal()
    }
  }

  #reveal() {
    this.downAccum = 0
    this.upAccum = 0
    document.body.classList.remove("tabbar-hidden")
  }

  #conceal() {
    document.body.classList.add("tabbar-hidden")
  }

  refresh() {
    const path = window.location.pathname
    const links = this.linkTargets
    let active = links.find((link) => this.#matches(link.dataset.tabPath, path))
    // Sin match (/:slug de una tienda, carrito, checkout): el contexto es
    // browsing, igual que el stack del tab Tiendas en native
    if (!active) active = links.find((link) => link.dataset.tabPath === "/")
    this.#paint(active)
  }

  // Marcado optimista: pinta el tab al tocarlo, sin esperar la visita de Turbo
  // (como una tab bar nativa); el turbo:load de la página nueva confirma o
  // corrige. Con modificadores (nueva pestaña) no aplica: esta pestaña no navega
  select(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    this.#paint(event.currentTarget)
  }

  #paint(active) {
    this.linkTargets.forEach((link) => {
      const isActive = link === active
      link.classList.toggle("is-active", isActive)
      if (isActive) {
        link.setAttribute("aria-current", "page")
      } else {
        link.removeAttribute("aria-current")
      }

      const icon = link.querySelector(".bi")
      if (icon) {
        icon.classList.remove(link.dataset.icon, link.dataset.iconActive)
        icon.classList.add(isActive ? link.dataset.iconActive : link.dataset.icon)
      }
    })
  }

  #matches(own, path) {
    if (own === "/") return path === "/" || path.startsWith("/stores")
    return path === own || path.startsWith(`${own}/`)
  }
}
